import { useState, Dispatch, SetStateAction } from 'react';

import ConfirmationModal from '@apigrowfinance/pbolt-confirmation-modal';
import { PDialog } from '@apigrowfinance/pbolt-dialog';
// eslint-disable-next-line import/no-unresolved
import { Notify } from 'notify/services/Notify';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { AddressDetails, ILocationFormInputs } from '@clients/accountsClient';
import LocationDetailsForm from '@components/locations/LocationDetailsForm';
import LocationSelectionList from '@components/locations/LocationSelectionList';
import { useAccount } from '@contexts/AccountProvider';
import useEditLocation from '@hooks/queries/useEditLocation';
import {
    getCountryTranslations,
    SupportedLocale,
} from '@utils/countryTranslations';

const notify = Notify.getInstance();

const formDefaultValues: ILocationFormInputs = {
    type: '',
    address: '',
    complementary_address: '',
    city: '',
    neighborhood: '',
    zip_code: '',
    country: '',
    mailing_address: undefined,
} as const;

export type EditLocationDetailsProps = {
    locations: AddressDetails[];
    setEditLocationOpen: Dispatch<SetStateAction<boolean>>;
    // TODO refactor and handle suppression
    // eslint-disable-next-line react/no-unused-prop-types
    editLocationOpen: boolean;
    refetch: () => void;
};

const EditLocationDetails = ({
    locations,
    setEditLocationOpen,
    refetch,
}: EditLocationDetailsProps) => {
    const { locale, formatMessage } = useIntl();
    const { accountId } = useParams();
    const [stage, setStage] = useState<'selection' | 'form' | 'confirmation'>(
        'selection',
    );
    const [locationFormDetails, setLocationFormDetails] = useState(null);
    const [formDefaults, setLocationFormDefaults] = useState(formDefaultValues);
    const [selectedAddress, setSelectedAddress] = useState('');

    const { account } = useAccount();

    const { isLoading, mutate } = useEditLocation(
        +accountId,
        +selectedAddress,
        () => {
            notify.addNotification(
                formatMessage({
                    id: 'location.updated',
                    defaultMessage: 'Location updated',
                }),
            );
            handleCloseForm();
            refetch();
        },
    );

    const submitDeleteLocation = () => {
        mutate({ ...locationFormDetails, active: false });
    };

    const readyToSubmitData = (data: AddressDetails) => {
        setLocationFormDetails(data);
        setLocationFormDefaults(data);
        mutate(data);
    };

    const handleDeleteLocation = () => {
        setStage('confirmation');
    };

    const cancelChange = () => {
        setStage('selection');
    };

    const handleCloseEditForm = () => {
        setStage('selection');
    };

    const handleCloseForm = () => {
        setEditLocationOpen(false);
    };

    const updateSelectedAddress = (addressId: string) => {
        const foundLocation = locations.filter(
            (location) => location.id === +addressId,
        );
        const addressMatch = foundLocation[0];

        setLocationFormDefaults({
            type: addressMatch?.type || 'RESIDENTIAL',
            address: addressMatch.address,
            complementary_address: addressMatch.complementary_address,
            city: addressMatch.city,
            neighborhood: addressMatch.neighborhood,
            zip_code: addressMatch.zip_code,
            country: addressMatch.country,
            mailing_address: addressMatch.mailing_address,
        });
        setSelectedAddress(addressId);
        setStage('form');
    };

    return (
        <>
            <PDialog
                open={stage === 'selection'}
                dialogTitle={
                    <FormattedMessage
                        id="EditLocation.selectionTitle"
                        defaultMessage={"Edit {accountName}'s locations"}
                        values={{ accountName: account?.name }}
                    />
                }
                dialogSubtitle={
                    <FormattedMessage
                        id="EditLocation.selection"
                        defaultMessage="You can edit the details by selecting the more icon at the end of the row."
                    />
                }
                onClose={handleCloseForm}>
                <LocationSelectionList
                    locations={locations}
                    handleDeleteLocation={handleDeleteLocation}
                    updateSelectedAddress={updateSelectedAddress}
                />
            </PDialog>

            <PDialog
                open={stage === 'form'}
                dialogTitle={
                    <FormattedMessage
                        id="EditLocation.formTitle"
                        defaultMessage="Edit address details"
                    />
                }
                dialogSubtitle={
                    <FormattedMessage
                        id="EditLocation.changeDesc"
                        defaultMessage="You can edit the details by changing the input fields below then clicking save."
                    />
                }
                onClose={handleCloseForm}>
                <LocationDetailsForm
                    closeModal={handleCloseEditForm}
                    readyToSubmitData={readyToSubmitData}
                    formDefaults={formDefaults}
                    formAction="EDIT"
                    isLoading={isLoading}
                />
            </PDialog>

            <ConfirmationModal
                title={
                    <FormattedMessage
                        id="EditLocation.ConfirmationTitle"
                        defaultMessage="Are you sure?"
                    />
                }
                description={
                    <FormattedMessage
                        id="EditLocation.ConfirmationDesc"
                        defaultMessage={
                            "Are you sure you want to mark ''{accountName}'' location ''{newLocation}'' as inactive?"
                        }
                        values={{
                            accountName: account?.name,
                            newLocation: [
                                formDefaults?.address,
                                formDefaults?.complementary_address,
                                formDefaults?.neighborhood,
                                formDefaults?.city,
                                getCountryTranslations(
                                    formDefaults?.country,
                                    locale as SupportedLocale,
                                ),
                                formDefaults?.zip_code,
                            ]
                                .filter(Boolean)
                                .join(', '),
                        }}
                    />
                }
                open={stage === 'confirmation'}
                positiveCallback={submitDeleteLocation}
                negativeCallback={cancelChange}
                positiveButtonText={
                    <FormattedMessage
                        id="addLocationForm.proceed"
                        defaultMessage="Yes, proceed"
                    />
                }
                negativeButtonText={
                    <FormattedMessage
                        id="addLocationFrom.NoCancel"
                        defaultMessage="No, cancel"
                    />
                }
            />
        </>
    );
};

export default EditLocationDetails;
